import { ReactNode, Suspense } from 'react'
import { selectUser } from '@features/auth/auth-slice'
import { useUser } from '@features/auth/hooks'
import NotFound from '@pages/not-found'
import { SEO } from '@common/components'
import {
  COMPANY_ACCOUNT_ROUTE,
  EVENT_ROUTE,
  LOGIN_ROUTE,
  MY_CLASSROOMS_ROUTE,
  PRICING_ROUTE,
  SIMPLE_LOGIN_ROUTE,
} from '@common/constants/routes'
import { useAppSelector } from '@common/hooks'
import { Navigate, useLocation, useNavigate, useNavigationType } from '@lib/router'

interface PageLayoutProps {
  children: ReactNode
  title: string
  description?: string
  keywords?: string
  image?: string
  noIndex?: boolean
  fallback: ReactNode
  protected?: boolean
  public?: boolean
  redirectMessage?: string
  roles?: string[]
  simple?: boolean
}

const PageLayout = (props: PageLayoutProps) => {
  const {
    title,
    description,
    keywords,
    image,
    noIndex,
    fallback,
    protected: protectedRoute,
    public: publicRoute,
    simple = false,
    redirectMessage,
    children,
    roles,
  } = props
  const user = useAppSelector(selectUser)
  const { isTeacher, isStudent, isCompany } = useUser()
  const location = useLocation()
  const navigationType = useNavigationType()
  const navigate = useNavigate()
  const { state } = location
  const previousPathname = state?.from || '/'

  // Handle simple login route edge case
  if (
    location.pathname === SIMPLE_LOGIN_ROUTE &&
    previousPathname?.pathname?.startsWith(EVENT_ROUTE)
  ) {
    navigate('/')
  }

  // Authentication checks
  const defaultMessage = 'You have to login first'
  const navigationState = {
    message: redirectMessage ?? defaultMessage,
    from: location,
  }

  // Protected route check
  if (protectedRoute && !user) {
    const loginRoute = simple ? SIMPLE_LOGIN_ROUTE : LOGIN_ROUTE
    return <Navigate to={loginRoute} state={navigationState} />
  }

  // Public route check for verified users
  if (publicRoute && user?.verified) {
    if (location.pathname === '/verify') {
      if (isTeacher) return <Navigate to={PRICING_ROUTE} state={navigationState} />
      if (isCompany) return <Navigate to={COMPANY_ACCOUNT_ROUTE} state={navigationState} />
    }

    const targetRoute =
      navigationType === 'POP' ||
      [LOGIN_ROUTE, SIMPLE_LOGIN_ROUTE, '/'].includes(previousPathname.pathname)
        ? MY_CLASSROOMS_ROUTE + '#english'
        : // ? HOME_ROUTE
          previousPathname

    return <Navigate to={targetRoute} state={navigationState} />
  }

  // Role-based access check
  if (roles && user) {
    const hasAllowedRole =
      (roles.includes('TEACHER') && isTeacher) || (roles.includes('STUDENT') && isStudent)

    if (!hasAllowedRole) {
      return <NotFound sx={{ mt: -15 }} />
    }
  }

  // Generate page URL for SEO
  const currentUrl = `https://www.speakduo.com${location.pathname}`

  // Default render
  return (
    <Suspense fallback={fallback}>
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        image={image}
        url={currentUrl}
        noIndex={noIndex}
      />
      {children}
    </Suspense>
  )
}

export default PageLayout
